import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material'
import { Groups } from '@mui/icons-material'
import { DASHBOARD_HOME } from '@yourownmetric/shared'

import { useGetProjectConfigQuery, useGetUsageQuery, useListSubTenantsQuery } from '@/apis/v2Api'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { setSubTenant } from '@/app/projectSlice'
import { EmptyState } from '@/components/shared/EmptyState'
import { useProjectId, useProjectToday } from '@/hooks/useReportArgs'
import { periodToRange, type Period } from '@/utils/dates'

import { ReportHeader, ReportStatus, fmtDateTime, fmtNum } from './common'

interface Row {
    externalId: string
    name: string
    status: string | null
    createdAt: string | null
    events: number
    sessions: number
}

/** v2 `/sub-tenants` (registered) merged with `/reports/usage` (who sent events). */
export function SubTenantsPage() {
    const [period, setPeriod] = useState<Period>('30d')
    const [q, setQ] = useState('')
    const projectId = useProjectId()
    const current = useAppSelector(s => s.project.subTenant)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const today = useProjectToday()
    const { from, to } = periodToRange(period, today)

    const list = useListSubTenantsQuery({ projectId })
    const cfg = useGetProjectConfigQuery(projectId)
    // Usage is always project-wide here (all sub-tenants), whatever the global filter is.
    const usage = useGetUsageQuery({ projectId, from, to, subTenant: null })

    const rows = useMemo<Row[]>(() => {
        const byId = new Map<string, Row>()
        for (const st of list.data ?? []) {
            byId.set(st.external_id, {
                externalId: st.external_id,
                name: st.name,
                status: st.status,
                createdAt: st.created_at,
                events: 0,
                sessions: 0,
            })
        }
        for (const u of usage.data?.rows ?? []) {
            if (u.key === '') {
                continue
            }
            const r = byId.get(u.key) ?? {
                externalId: u.key,
                name: '',
                status: null,
                createdAt: null,
                events: 0,
                sessions: 0,
            }
            r.events = u.events
            r.sessions = u.sessions
            byId.set(u.key, r)
        }
        const needle = q.trim().toLowerCase()
        return [...byId.values()]
            .filter(
                r =>
                    !needle ||
                    r.externalId.toLowerCase().includes(needle) ||
                    r.name.toLowerCase().includes(needle),
            )
            .sort((a, b) => b.events - a.events || a.externalId.localeCompare(b.externalId))
    }, [list.data, usage.data, q])

    const own = usage.data?.rows.find(r => r.key === '')
    const mode = cfg.data?.config.subTenants.mode
    const view = (st: string | null) => {
        dispatch(setSubTenant(st))
        navigate(DASHBOARD_HOME)
    }

    return (
        <Box sx={{ p: 3 }}>
            <ReportHeader
                title="Sub-tenants"
                subtitle="End-customers of this project. Pick one to filter every report by it."
                period={period}
                onPeriodChange={setPeriod}
                actions={
                    <TextField
                        size="small"
                        placeholder="Search id or name"
                        value={q}
                        onChange={e => setQ(e.target.value)}
                    />
                }
            />
            {mode === 'off' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Sub-tenants are off for this project (config <code>subTenants.mode</code>):
                    events are not partitioned. Turn on <code>open</code> or <code>registered</code>{' '}
                    via the v2 config API to use them.
                </Alert>
            )}
            <ReportStatus
                isLoading={list.isLoading || usage.isLoading}
                error={list.error ?? usage.error}
            >
                {rows.length === 0 && !own ? (
                    <EmptyState
                        icon={<Groups />}
                        title="No sub-tenants yet"
                        description="Register sub-tenants with PUT /v2/projects/:id/sub-tenants/:externalId, or send events with an `st` field (open mode)."
                    />
                ) : (
                    <Card>
                        <CardContent>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Sub-tenant</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Registered</TableCell>
                                        <TableCell align="right">Events</TableCell>
                                        <TableCell align="right">Sessions</TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {own && (
                                        <TableRow selected={current === ''}>
                                            <TableCell>
                                                <Typography variant="body2" fontStyle="italic">
                                                    (project itself)
                                                </Typography>
                                            </TableCell>
                                            <TableCell />
                                            <TableCell />
                                            <TableCell />
                                            <TableCell align="right">
                                                {fmtNum(own.events)}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtNum(own.sessions)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button size="small" onClick={() => view('')}>
                                                    View reports
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    {rows.map(r => (
                                        <TableRow
                                            key={r.externalId}
                                            hover
                                            selected={current === r.externalId}
                                        >
                                            <TableCell sx={{ fontFamily: 'monospace' }}>
                                                {r.externalId}
                                            </TableCell>
                                            <TableCell>{r.name}</TableCell>
                                            <TableCell>
                                                {r.status ? (
                                                    <Chip
                                                        size="small"
                                                        label={r.status}
                                                        color={
                                                            r.status === 'active'
                                                                ? 'success'
                                                                : 'default'
                                                        }
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label="unregistered"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.createdAt ? fmtDateTime(r.createdAt) : '—'}
                                            </TableCell>
                                            <TableCell align="right">{fmtNum(r.events)}</TableCell>
                                            <TableCell align="right">
                                                {fmtNum(r.sessions)}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    onClick={() => view(r.externalId)}
                                                >
                                                    View reports
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                )}
            </ReportStatus>
        </Box>
    )
}
